import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ADD_OBSERVATION_COLUMNS_DATA,
  INVESTIGATION_OBSERVATION_COLUMNS_DATA,
} from "./config";
import { useAlert } from "context/alert";
import { useSelector } from "react-redux";
import TitleComponent from "./TitleComponet";
import InvestigationResults from "./InvestigationResults";
import Table from "components/Common/Table/index";
import {
  createPatientLabResultEntry,
  fetchInvestigationObservationDropDown,
  fetchObservationResultEntryData,
  fetchOnlyOrganismData,
  fetchReportRoleById,
} from "services/resultEntryReportMaster";
import InternalDoctorCheckbox from "components/Common/Checkbox/InternalDoctorCheckbox";
import Loader from "components/Common/loader";
import { STALE_TIME } from "utils/constants";
import CommentNameDropdown from "components/Common/Dropdowns/CommentNameDropdown";
import TextEditor from "components/Common/TextEditor/editor";
import Swal from "sweetalert2";
import TemplateDropDown from "components/Common/Dropdowns/TemplateDropDown";
import zoneLocalStorageId from "utils/zone";
import { getInvestigationComment, getResultEntrySaveError } from "utils/resultEntryComments";
import { calculateResultTree, calculationContext, isCalculatedObservation, isLockedObservationStatus, isManualSelfConversion } from "utils/resultEntryCalculations";

function cloneObservationDraft(data) {
  return JSON.parse(JSON.stringify(data || {}));
}

function hasObservationRows(data) {
  return Object.values(data || {}).some((investigations) =>
    Object.values(investigations || {}).some((wrapper) => wrapper?.observations?.length > 0)
  );
}

function createInitialObservationDraft(data) {
  const draft = cloneObservationDraft(data);

  Object.values(draft).forEach((investigations) => {
    Object.values(investigations || {}).forEach((wrapper) => {
      (wrapper?.observations || []).forEach((observation) => {
        if (isLockedObservationStatus(observation.status)) return;
        const hasStoredValue =
          observation.observation_value !== undefined &&
          observation.observation_value !== null;
        const hasMachineValue =
          observation.machine_reading !== undefined &&
          observation.machine_reading !== null &&
          String(observation.machine_reading) !== "";

        if (!hasStoredValue && hasMachineValue) {
          observation.observation_value = String(observation.machine_reading);
        } else if (!hasStoredValue && observation.default_reading != null) {
          observation.observation_value = String(observation.default_reading);
        }
      });
    });
  });

  return draft;
}

function buildCheckedInvestigations(data) {
  const nextChecked = {};

  Object.entries(data || {}).forEach(([department, investigations]) => {
    Object.keys(investigations || {}).forEach((investigationName) => {
      nextChecked[`${department}-${investigationName}`] = true;
    });
  });

  return nextChecked;
}

function updateObservationFieldInDraft(
  draft,
  department,
  investigation,
  obsId,
  field,
  value
) {
  const currentInvestigation = draft?.[department]?.[investigation];
  if (!currentInvestigation?.observations?.length) {
    return draft;
  }

  let changed = false;
  const nextObservations = currentInvestigation.observations.map((obs) =>
    +obs.observation_id === +obsId
      ? (() => {
          if (obs[field] === value) {
            return obs;
          }
          changed = true;
          return { ...obs, [field]: value };
        })()
      : obs
  );

  if (!changed) {
    return draft;
  }

  return {
    ...draft,
    [department]: {
      ...draft[department],
      [investigation]: {
        ...currentInvestigation,
        observations: nextObservations,
      },
    },
  };
}

const MemoizedObservationTable = React.memo(function MemoizedObservationTable({
  data,
  tableColumns,
  isLoading = false,
}) {
  return (
    <Table
      data={data}
      isLoading={isLoading}
      tableColumns={tableColumns}
      showPagination={false}
    />
  );
});

const AddObservationValue = ({
  onClose,
  rowData,
  handleDocuments,
  documentInfo,
  refetchBussinessReport,
  isOpenObservationValue,
  onSaveSuccess,
}) => {
  const { showAlert } = useAlert();
  const jwtAuthreducer = useSelector((state) => state.jwtAuthreducer);
  const [filterValue, setFilterValue] = useState();
  const [doctorId, setdoctorId] = useState();
  const [investigationId, setInvestigationId] = useState(
    rowData?.investigation_data?.investigation_id
  );
  const [observationDraft, setObservationDraft] = useState({});
  const [commentsMap, setCommentsMap] = useState({});
  const [commentNamesMap, setCommentNamesMap] = useState({});
  const [status, setStatus] = useState(null);
  const [selectedInvestigationIds, setSelectedInvestigationIds] = useState([]);
  const [selectedInvestigations, setSelectedInvestigations] = useState([]);
  const [investigationDataMap, setInvestigationDataMap] = useState({});
  const [template, setTemplate] = useState({});
  const [checkedInvestigations, setCheckedInvestigations] = useState({});
  const [observationData, setObservationData] = useState([]);
  const [isObservationSectionReady, setIsObservationSectionReady] =
    useState(false);
  const observationDraftRef = useRef({});
  const saveInFlightRef = useRef(false);

  useEffect(() => {
    if (!isOpenObservationValue) {
      observationDraftRef.current = {};
      setObservationDraft({});
      setCheckedInvestigations({});
      return;
    }

    const nextInvestigationId = rowData?.investigation_data?.investigation_id;
    setInvestigationId(nextInvestigationId);
    observationDraftRef.current = {};
    setObservationDraft({});
    setCommentsMap({});
    setCommentNamesMap({});
    setTemplate({});
    setFilterValue(undefined);
    setdoctorId(undefined);
    setStatus(null);
    setSelectedInvestigationIds([]);
    setSelectedInvestigations([]);
    setInvestigationDataMap({});
    setObservationData([]);
    setIsObservationSectionReady(false);
    setCheckedInvestigations({});
  }, [
    isOpenObservationValue,
    rowData?.appointment_id,
    rowData?.investigation_data?.investigation_id,
  ]);

  const handleCheckboxChange = (key) => {
    setCheckedInvestigations((prev) => {
      return {
        ...prev,
        [key]: !Boolean(prev[key]),
      };
    });
  };
  const auth = useSelector((state) => state.jwtAuthreducer);

  const onSuccess = async () => {
    saveInFlightRef.current = false;
    const document = true;
    const message = rowData
      ? "Document updated successfully!"
      : "Document created successfully!";
    showAlert(message, "success");
    refetchPayment();
    if (typeof onSaveSuccess === "function") {
      await onSaveSuccess(rowData);
    } else {
      refetchBussinessReport?.();
    }
    onClose(document);
  };

  const onError = (error) => {
    saveInFlightRef.current = false;
    showAlert(getResultEntrySaveError(error), "danger");
  };

  const {
    data: listPayment,
    isLoading: isLoadingPayment,
    isFetching: isFetchingObservations,
    error: observationError,
    refetch: refetchPayment,
  } = useQuery(
    [
      "fetch-observation-result-entry",
      rowData?.appointment_id,
      investigationId,
    ],
    () =>
      fetchObservationResultEntryData({
        appointment_id: rowData?.appointment_id,
        investigation_id: investigationId,
      }),
    {
      staleTime: 0,
      select: (res) => {
        const response = res?.data?.data;
        if (response && response.length) {
          res.data.data = response.map((x, index) => ({
            ...x,
          }));
        }
        return res;
      },
      onError: (error) => {
        console.error("Error fetching payments:", error);
        Swal.fire({
          icon: "error",
          title: "Failed to Fetch Data",
          text:
            error?.response?.data?.detail ||
            "Something went wrong while fetching payment data.",
        });
      },
      enabled: isOpenObservationValue && !!rowData?.appointment_id && !!investigationId,
    }
  );

  const headerDetails = listPayment?.data?.header_details;
  const canOverrideCalculation = listPayment?.data?.calculation_policy_version === 1 &&
    listPayment?.data?.can_override_calculation === true;
  const patientAgeForFormula = useMemo(() => {
    if (!headerDetails || headerDetails.age === undefined || headerDetails.age === null) {
      return null;
    }
    const ageRaw = Number(headerDetails.age);
    if (!Number.isFinite(ageRaw)) return null;
    // This endpoint's age contract is days, including children younger than 150 days.
    const ageInYears = ageRaw / 365.25;
    return ageInYears <= 0 || ageInYears > 120 ? null : ageInYears;
  }, [headerDetails]);

  const calculationSnapshot = useMemo(() => calculateResultTree(observationDraft, {
    ageYears: patientAgeForFormula, gender: headerDetails?.gender,
  }), [observationDraft, patientAgeForFormula, headerDetails?.gender]);
  const calculatedDraft = calculationSnapshot.tree;

  // console.log("listPayment?.data?.observations", listPayment?.data);

  const hasAnyCulture = useMemo(() => {
    if (
      !observationDraft ||
      Object.keys(observationDraft).length === 0
    ) {
      return false;
    }

    return Object.entries(observationDraft).some(
      ([department, investigations]) =>
        Object.entries(investigations).some(
          ([investigation_name, observationWrapper]) =>
            observationWrapper?.investigation_detail?.is_culture === true
        )
    );
  }, [observationDraft]);

  const hasNegativeObservationValue = useMemo(() => {
  if (status === "NOT_APPROVED" || !calculatedDraft || Object.keys(calculatedDraft).length === 0) {
    return false;
  }

  return Object.entries(calculatedDraft).some(
    ([department, investigations]) =>
      Object.entries(investigations).some(
        ([investigation_name, observationWrapper]) => {
          const investigationKey = `${department}-${investigation_name}`;
          // Only checked (i.e. actually-submitted) investigations count
          if (checkedInvestigations[investigationKey] !== true) {
            return false;
          }

          const observations = observationWrapper?.observations || [];
          return observations.some((obs) => {
            if (obs.header === true || isLockedObservationStatus(obs.status)) return false;
            const value = obs.observation_value;
            if (value === undefined || value === null || value === "") {
              return false;
            }
            const numeric = Number(value);
            return Number.isFinite(numeric) && numeric < 0;
          });
        }
      )
  );
  }, [calculatedDraft, checkedInvestigations, status]);

  const isInvestigationCulture = (department, investigationName) => {
    if (!observationDraft || !observationDraft[department]) {
      return false;
    }

    const investigation = observationDraft[department][investigationName];
    return investigation?.investigation_detail?.is_culture === true;
  };

  const {
    data: onlyOrganismData,
  } = useQuery(["fetch-only-organism-data"], () => fetchOnlyOrganismData(), {
    staleTime: STALE_TIME,
    select: (res) => {
      const response = res?.data?.data;
      if (response && response.length) {
        res.data.data = response.map((x, index) => ({
          ...x,
        }));
      }
      return res;
    },
    onError: (error) => {
      console.error("Error fetching payments:", error);
      Swal.fire({
        icon: "error",
        title: "Failed to Fetch Data",
        text:
          error?.response?.data?.detail ||
          "Something went wrong while fetching payment data.",
      });
    },
    enabled: hasAnyCulture,
  });

  // const {
  //   data: investigationObservationData,
  //   isLoading: isLoadingDropDown,
  //   refetch: refetchDropDown,
  // } = useQuery(
  //   ["fetch-observation-result-entry-drop-down", selectedInvestigationId],
  //   () =>
  //     fetchInvestigationObservationDropDown({
  //       investigation_id: selectedInvestigationId,
  //       req_help_data: true,
  //     }),
  //   {
  //     staleTime: STALE_TIME,
  //     enabled:
  //       !!selectedInvestigationId &&
  //       listPayment?.data?.header_details?.is_culture === true,
  //     onSuccess: (res) => {
  //       const response = res?.data?.data || [];

  //       setInvestigationDataMap((prevData) => ({
  //         ...prevData,
  //         [selectedInvestigationId]: response,
  //       }));
  //     },
  //     onError: (error) => {
  //       console.error("Error fetching payments:", error);
  //       Swal.fire({
  //         icon: "error",
  //         title: "Failed to Fetch Data",
  //         text:
  //           error?.response?.data?.detail ||
  //           "Something went wrong while fetching payment data.",
  //       });
  //     },
  //   }
  // );

  // useEffect(() => {
  //   selectedInvestigationIds.forEach((investigationId) => {
  //     if (listPayment?.data?.header_details?.is_culture) {
  //       fetchInvestigationObservationDropDown({
  //         investigation_id: investigationId,
  //         req_help_data: true,
  //       })
  //         .then((res) => {
  //           const response = res?.data?.data || [];

  //           // **Yahaan bhi merge karo na ki pura replace karo**
  //           setInvestigationDataMap((prevData) => ({
  //             ...prevData,
  //             [investigationId]: [
  //               // Pehle se existing data (agar koi hai)
  //               ...(prevData[investigationId] || []),
  //               // API response wala data (ya to replace karna ho to bas response hi rakh do)
  //               ...response,
  //             ],
  //           }));
  //         })
  //         .catch((error) => {
  //           console.error("Error fetching investigation data:", error);
  //           Swal.fire({
  //             icon: "error",
  //             title: "Failed to Fetch Data",
  //             text:
  //               error?.response?.data?.detail ||
  //               "Something went wrong while fetching investigation data.",
  //           });
  //         });
  //     }
  //   });
  // }, [selectedInvestigationIds, listPayment?.data?.header_details?.is_culture]);

  useEffect(() => {
    selectedInvestigationIds.forEach((investigationId) => {
      if (hasAnyCulture) {
        fetchInvestigationObservationDropDown({
          investigation_id: investigationId,
          req_help_data: true,
        })
          .then((res) => {
            const response = res?.data?.data || [];

            setInvestigationDataMap((prevData) => {
              const existingData = prevData[investigationId] || [];

              const combined = [...existingData, ...response];

              const uniqueData = combined.filter(
                (item, index, self) =>
                  index ===
                  self.findIndex(
                    (t) =>
                      t.investigation_id === item.investigation_id &&
                      t.observation_id === item.observation_id
                  )
              );

              return {
                ...prevData,
                [investigationId]: uniqueData,
              };
            });
          })
          .catch((error) => {
            console.error("Error fetching investigation data:", error);
            Swal.fire({
              icon: "error",
              title: "Failed to Fetch Data",
              text:
                error?.response?.data?.detail ||
                "Something went wrong while fetching investigation data.",
            });
          });
      }
    });
  }, [selectedInvestigationIds, hasAnyCulture]);

  const handleInvestigationChange = (event) => {
    const selectedId = event.target.value;
    if (!selectedId) return;

    // console.log("selectedInvestigations", selectedInvestigations);

    if (
      selectedInvestigations.some(
        (item) => String(item.id) === String(selectedId)
      )
    ) {
      Swal.fire({
        icon: "warning",
        title: "Already Selected",
        text: "This investigation is already added!",
      });
      return;
    }

    const selectedOption = onlyOrganismData?.data?.data?.find(
      (item) => String(item.id) === String(selectedId)
    );

    if (selectedOption) {
      setSelectedInvestigations((prev) => [
        ...prev,
        {
          id: selectedOption.id,
          name: selectedOption.investigation_name,
        },
      ]);
      setSelectedInvestigationIds((prev) => [
        ...new Set([...prev, selectedOption.id]),
      ]);
    }
  };

  const handleRemoveInvestigation = (id) => {
    setSelectedInvestigations((prev) => prev.filter((inv) => inv.id !== id));
    setInvestigationDataMap((prevData) => {
      const updatedData = { ...prevData };
      delete updatedData[id];
      return updatedData;
    });
  };

  const inputRefs = useRef([]);
  const lastFocusedInput = useRef(null);
  const isNavigatingWithinTableRef = useRef(false);
  const observationLoadKeyRef = useRef(null);
  const observationMetaLoadKeyRef = useRef(null);
  const isOrganismCultureActiveRef = useRef(false);
  const organismDataSignatureRef = useRef("");

  useEffect(() => {
    if (!isOpenObservationValue) {
      observationLoadKeyRef.current = null;
      observationMetaLoadKeyRef.current = null;
      isOrganismCultureActiveRef.current = false;
      organismDataSignatureRef.current = "";
      setIsObservationSectionReady(false);
      return;
    }

    const departmentData =
      listPayment?.data?.observations?.department_investigation;
    if (isFetchingObservations || !departmentData || !rowData?.appointment_id || !investigationId) {
      return;
    }

    const loadKey = `${rowData.appointment_id}-${investigationId}`;
    const hasExistingDraft = hasObservationRows(observationDraft);

    // An empty response is initialized too; do not keep creating new empty drafts.
    if (observationLoadKeyRef.current === loadKey &&
        (hasExistingDraft || !hasObservationRows(departmentData))) {
      return;
    }

    const initialDraft = createInitialObservationDraft(departmentData);

    observationLoadKeyRef.current = loadKey;
    observationMetaLoadKeyRef.current = null;
    isOrganismCultureActiveRef.current = false;
    organismDataSignatureRef.current = "";
    inputRefs.current = [];
    setIsObservationSectionReady(false);
    observationDraftRef.current = initialDraft;
    setObservationDraft(initialDraft);
    setCheckedInvestigations(buildCheckedInvestigations(initialDraft));
  }, [
    isOpenObservationValue,
    rowData?.appointment_id,
    investigationId,
    listPayment?.data?.observations?.department_investigation,
    isFetchingObservations,
    observationDraft,
  ]);

  // const dataRef = useRef(null);

  // useEffect(() => {
  //   console.log("trigger");

  //   const currentData =
  //     listPayment?.data?.observations?.department_investigation;
  //   const currentDataString = JSON.stringify(currentData);

  //   if (
  //     isOpenObservationValue &&
  //     currentData &&
  //     currentDataString !== dataRef.current
  //   ) {
  //     dispatch(setResultEntryObservations(currentData));
  //     dataRef.current = currentDataString;
  //   }
  // }, [
  //   isOpenObservationValue,
  //   listPayment?.data?.observations?.department_investigation,
  // ]);

  // useEffect(() => {
  //   if (!isOpenObservationValue) {
  //     dataRef.current = null;
  //   }
  // }, [isOpenObservationValue]);

  // useEffect(() => {
  //   if (
  //     listPayment?.data?.observation_organism &&
  //     listPayment.data.observation_organism.length > 0
  //   ) {
  //     setIsOrganismData(true);
  //     setOrganismObservations(listPayment.data.observation_organism);

  //     const organismInvestigationIds =
  //       listPayment.data.observation_organism.map(
  //         (item) => item.investigation_id
  //       );

  //     const uniqueInvestigationIds = [...new Set(organismInvestigationIds)];

  //     setSelectedInvestigationIds(uniqueInvestigationIds);

  //     uniqueInvestigationIds.forEach((investigationId) => {
  //       const investigationData = listPayment.data.observation_organism.filter(
  //         (item) => item.investigation_id === investigationId
  //       );

  //       const investigationInfo = onlyOrganismData?.data?.data?.find(
  //         (item) => item.id === investigationId
  //       );

  //       if (investigationInfo) {
  //         setSelectedInvestigations((prev) => {
  //           const exists = prev.some((inv) => inv.id === investigationId);
  //           if (!exists) {
  //             return [
  //               ...prev,
  //               {
  //                 id: investigationId,
  //                 name: investigationInfo.investigation_name,
  //               },
  //             ];
  //           }
  //           return prev;
  //         });

  //         // **Merge kar rahe hain previous data ke sath**
  //         setInvestigationDataMap((prev) => ({
  //           ...prev,
  //           // Agar pehle se data hai to use bhi merge kar do
  //           [investigationId]: [
  //             ...(prev[investigationId] || []),
  //             ...investigationData,
  //           ],
  //         }));
  //       }
  //     });
  //   } else {
  //     setIsOrganismData(false);
  //     setOrganismObservations([]);
  //     setSelectedInvestigationIds([]);
  //     setInvestigationDataMap({}); // Clear bhi kar sakte ho agar data nahi hai
  //   }
  // }, [listPayment?.data?.observation_organism]);

  useEffect(() => {
    let observationOrganismData = [];

    Object.entries(observationDraft).forEach(
      ([department, investigations]) => {
        Object.entries(investigations).forEach(
          ([investigation_name, observationWrapper]) => {
            if (
              observationWrapper?.investigation_organism &&
              observationWrapper.investigation_organism.length > 0
            ) {
              observationOrganismData = [
                ...observationOrganismData,
                ...observationWrapper.investigation_organism,
              ];
            }
          }
        );
      }
    );

    const organismSignature = observationOrganismData
      .map((item) => `${item.investigation_id}-${item.observation_id}`)
      .join("|");

    if (observationOrganismData.length > 0) {
      if (organismSignature === organismDataSignatureRef.current) {
        return;
      }
      organismDataSignatureRef.current = organismSignature;
      isOrganismCultureActiveRef.current = true;

      const organismInvestigationIds = observationOrganismData.map(
        (item) => item.investigation_id
      );

      const uniqueInvestigationIds = [...new Set(organismInvestigationIds)];

      setSelectedInvestigationIds(uniqueInvestigationIds);

      let allObservationData = [];

      uniqueInvestigationIds.forEach((investigationId) => {
        const investigationData = observationOrganismData.filter(
          (item) => item.investigation_id === investigationId
        );

        const investigationInfo = onlyOrganismData?.data?.data?.find(
          (item) => item.id === investigationId
        );

        if (investigationInfo) {
          setSelectedInvestigations((prev) => {
            const exists = prev.some((inv) => inv.id === investigationId);
            if (!exists) {
              return [
                ...prev,
                {
                  id: investigationId,
                  name: investigationInfo.investigation_name,
                },
              ];
            }
            return prev;
          });

          setInvestigationDataMap((prev) => {
            const existingData = prev[investigationId] || [];
            const combined = [...existingData, ...investigationData];

            const uniqueData = combined.filter(
              (item, index, self) =>
                index ===
                self.findIndex(
                  (t) =>
                    t.investigation_id === item.investigation_id &&
                    t.observation_id === item.observation_id
                )
            );

            const currentObservationData = uniqueData
              .filter(
                (item) =>
                  (item.observation_value !== undefined &&
                    item.observation_value !== null &&
                    String(item.observation_value).trim() !== "") ||
                  (item.observation_value_mac_reading !== undefined &&
                    item.observation_value_mac_reading !== null &&
                    String(item.observation_value_mac_reading).trim() !== "")
              )
              .map((item) => ({
                id: item.observation_id,
                parent_investigation_id: item.investigation_id,
                observation_value: item.observation_value || "",
                observation_value_mac_reading:
                  item.observation_value_mac_reading || "",
              }));

            allObservationData = [
              ...allObservationData,
              ...currentObservationData,
            ];

            allObservationData = allObservationData.filter(
              (item, index, self) =>
                index ===
                self.findIndex(
                  (t) =>
                    t.id === item.id &&
                    t.parent_investigation_id === item.parent_investigation_id
                )
            );

            setObservationData(allObservationData);

            return {
              ...prev,
              [investigationId]: uniqueData,
            };
          });
        }
      });
      return;
    }

    if (isOrganismCultureActiveRef.current) {
      isOrganismCultureActiveRef.current = false;
      organismDataSignatureRef.current = "";
      setSelectedInvestigationIds([]);
      setInvestigationDataMap({});
    }
  }, [observationDraft, onlyOrganismData?.data?.data]);

  // console.log(
  //   "listPayment?.data?.observation_organism",
  //   listPayment?.data?.observation_organism
  // );
  // console.log("invesfadsfdasfdsf", investigationDataMap);

  const {
    data: reportRoleData,
    isLoading,
  } = useQuery(
    ["fetch-report-role", jwtAuthreducer.user.employee_code_id],
    () =>
      fetchReportRoleById({
        employee_code_id: jwtAuthreducer.user.employee_code_id,
      }),
    {
      keepPreviousData: false,
      staleTime: STALE_TIME,
      select: (res) => {
        return res;
      },
      onError: (res) => {
        console.error("Error fetching areas:", res);
      },
    }
  );

  // console.log("reportRoleData", reportRoleData?.data);

  const { mutate: addPatientLabResultEntryMutate, isLoading: isLoadingAdd } =
    useMutation({
      mutationFn: createPatientLabResultEntry,
      onSuccess,
      onError,
    });

  // const handleSave = () => {
  //   if (
  //     !observationDataRedux ||
  //     Object.keys(observationDataRedux).length === 0
  //   ) {
  //     console.warn("No data available to save.");
  //     return;
  //   }

  //   if (status === "APPROVED" && !filterValue && !doctorId) {
  //     return;
  //   }

  //   const groupedData = {};
  //   let patientNameId = 0;
  //   let appointmentInvestigationId = 0;

  //   Object.entries(observationDataRedux).forEach(
  //     ([department, investigations]) => {
  //       Object.entries(investigations).forEach(
  //         ([investigation_name, observations]) => {
  //           const key = `${department}-${investigation_name}`;
  //           const currentComment = commentsMap[key] || "";
  //           const currentCommentName = commentNamesMap[key] || null;

  //           const investigationKey = `${department}-${investigation_name}`;
  //           const currentTemplate = template[investigationKey] || {};

  //           const allFilled = observations.every(
  //             (obs) =>
  //               obs.header === true ||
  //               (obs.observation_value !== undefined &&
  //                 obs.observation_value !== null &&
  //                 obs.observation_value !== "")
  //           );
  //           const currentStatus = allFilled ? "RESULT_DONE" : "INCOMPLETE";

  //           observations.forEach((obs) => {
  //             if (!currentTemplate?.template_id && !obs.observation_value) {
  //               return;
  //             }

  //             if (obs.patient_details?.id) {
  //               patientNameId = obs.patient_details.id;
  //             }

  //             if (obs.appointment_investigation_id) {
  //               appointmentInvestigationId = obs.appointment_investigation_id;
  //             }

  //             const appointment_id = obs.appointment_id || 0;
  //             const investigation_id = obs.investigation_id || 0;

  //             if (!groupedData[appointment_id]) {
  //               groupedData[appointment_id] = [];
  //             }

  //             let investigationEntry = groupedData[appointment_id].find(
  //               (entry) => entry.investigation_id === investigation_id
  //             );

  //             if (!investigationEntry) {
  //               investigationEntry = {
  //                 investigation_id,
  //                 appointment_id,
  //                 observation: [],
  //                 observation_organism: [],
  //               };
  //               groupedData[appointment_id].push(investigationEntry);
  //             }

  //             investigationEntry.observation.push({
  //               observation_id: obs.observation_id,
  //               gender: obs.patient_details?.gender,
  //               observation_value: String(obs.observation_value),
  //               status: status === "SAVE" ? currentStatus : status,
  //               min_reading: obs.min_reading,
  //               max_reading: obs.max_reading,
  //               min_critical: obs.min_critical,
  //               max_critical: obs.max_critical,
  //               unit: obs.unit,
  //               barcode_no: obs.barcode_no,
  //               doctor_id: filterValue,
  //               comments: stripHtmlTags(currentComment),
  //               comment_id: currentCommentName?.comment_id,
  //               modified_by: auth?.user?.employee_code_id,
  //               template_id: currentTemplate?.template_id,
  //               template_description: currentTemplate?.template_description,
  //               display_reading: obs.display_reading,
  //             });
  //           });
  //         }
  //       );
  //     }
  //   );

  //   if (observationData && observationData.length > 0) {
  //     Object.values(groupedData)
  //       .flat()
  //       .forEach((investigationEntry) => {
  //         observationData.forEach((obsData) => {
  //           if (
  //             obsData.observation_value !== undefined &&
  //             obsData.observation_value !== null &&
  //             String(obsData.observation_value).trim() !== ""
  //             // &&
  //             // obsData.observation_value_mac_reading !== undefined &&
  //             // obsData.observation_value_mac_reading !== null &&
  //             // String(obsData.observation_value_mac_reading).trim() !== ""
  //           ) {
  //             investigationEntry.observation_organism.push({
  //               patient_name_id: patientNameId,
  //               observation_id: obsData.id,
  //               investigation_id: obsData?.parent_investigation_id,
  //               parent_investigation_id: investigationEntry.investigation_id,
  //               appointment_id: investigationEntry.appointment_id,
  //               franchise_id: Number(localStorage.getItem("franchise_id")),
  //               zone_id: Number(zoneLocalStorageId),
  //               appointment_investigation_id: appointmentInvestigationId,
  //               gender: "",
  //               observation_value: String(obsData.observation_value),
  //               observation_value_mac_reading: String(
  //                 obsData.observation_value_mac_reading
  //               ),
  //               min_reading: "",
  //               max_reading: "",
  //               min_critical: "",
  //               max_critical: "",
  //               unit: "",
  //               is_active: true,
  //               created_by: auth?.user?.employee_code_id || 0,
  //             });
  //           }
  //         });
  //       });
  //   }

  //   const payload = {
  //     data: Object.values(groupedData).flat(),
  //   };

  //   if (payload.data.length > 0) {
  //     addPatientLabResultEntryMutate(payload, {
  //       onSuccess,
  //       onError,
  //     });
  //   }
  // };

  const handleSave = () => {
    if (saveInFlightRef.current || isLoadingAdd) return;
    const nextStatus = status || "SAVE";
    if (nextStatus === "APPROVED" && !filterValue && !doctorId) return;
    // Read synchronously updated inputs, not the previous render or a focused cell.
    const snapshot = calculateResultTree(observationDraftRef.current, {
      ageYears: patientAgeForFormula, gender: headerDetails?.gender,
    });
    const submissionTree = nextStatus === "NOT_APPROVED" ? observationDraftRef.current : snapshot.tree;
    const hasValue = (value) => value != null && String(value).trim() !== "";
    const shouldSubmit = (department, investigation, wrapper) => {
      if (!checkedInvestigations[`${department}-${investigation}`]) return false;
      const meaningful = (wrapper.observations || []).filter(
        (obs) => !obs.header && (hasValue(obs.observation_value) || isCalculatedObservation(obs))
      );
      return nextStatus === "NOT_APPROVED" || !meaningful.length ||
        !meaningful.every((obs) => isLockedObservationStatus(obs.status));
    };
    const selectedIssues = snapshot.issues.filter((issue) =>
      shouldSubmit(issue.department, issue.investigation, submissionTree[issue.department][issue.investigation])
    );
    if (selectedIssues.some(issue => issue.message.includes("review override"))) {
      showAlert("Inputs changed. Ask a result approver to review or reset the affected overrides before saving.", "danger");
      return;
    }
    if ((isLockedObservationStatus(nextStatus) || selectedIssues.some((issue) => issue.observationId == null)) && selectedIssues.length) {
      const issue = selectedIssues[0];
      showAlert(`Cannot approve ${issue.investigation}: observation ${issue.observationId ?? ""}: ${issue.message}. Check inputs and formula configuration.`, "danger");
      return;
    }
    for (const [department, investigations] of Object.entries(submissionTree)) {
      for (const [investigation, wrapper] of Object.entries(investigations)) {
        if (!shouldSubmit(department, investigation, wrapper)) continue;
        const rows = wrapper.observations || [];
        if (nextStatus !== "NOT_APPROVED" && rows.some((obs) =>
          !obs.header && hasValue(obs.observation_value) && Number(obs.observation_value) < 0)) {
          showAlert("Observation value cannot be negative. Please correct the inputs.", "danger");
          return;
        }
        // Validate each CBC independently; never add readings across packages.
        const cbc = rows.filter((obs) => [45, 46, 47, 48].includes(Number(obs.observation_id)));
        const cbcSum = cbc.reduce((sum, obs) => sum + (Number(obs.observation_value) || 0), 0);
        if (nextStatus !== "NOT_APPROVED" && cbcSum > 100) {
          showAlert(`CBC values invalid in ${investigation}: differential sum ${cbcSum.toFixed(2)} exceeds 100.`, "danger");
          return;
        }
      }
    }
    const getObservationValueForSave = (obs) => obs.observation_value;

    const groupedData = {};
    let patientNameId = 0;
    let appointmentInvestigationId = 0;

    Object.entries(submissionTree).forEach(
      ([department, investigations]) => {
        Object.entries(investigations).forEach(
          ([investigation_name, observationWrapper]) => {
            // Updated to handle new structure with observationWrapper
            const observations = observationWrapper?.observations || [];

            const investigationKey = `${department}-${investigation_name}`;
            if (!shouldSubmit(department, investigation_name, observationWrapper)) {
              return;
            }

            const currentComment = commentsMap[investigationKey] || "";
            const currentCommentName = commentNamesMap[investigationKey] || null;

            const currentTemplate = template[investigationKey] || {};

            const allFilled = observations.every((obs) => {
              if (obs.header === true) {
                return true;
              }
              const value = getObservationValueForSave(obs);
              return value !== undefined && value !== null && value !== "";
            });
            const currentStatus = allFilled ? "RESULT_DONE" : "INCOMPLETE";

            observations.forEach((obs) => {
              const observationValue = getObservationValueForSave(obs);
              // Include cleared values and numeric zero so old saved readings cannot survive.

              if (obs.patient_details?.id) {
                patientNameId = obs.patient_details.id;
              }

              if (obs.appointment_investigation_id) {
                appointmentInvestigationId = obs.appointment_investigation_id;
              }

              const appointment_id = obs.appointment_id || 0;
              // Updated to use investigation_details.id from new structure
              const investigation_id =
                obs.investigation_details?.id || obs.investigation_id || 0;

              if (!groupedData[appointment_id]) {
                groupedData[appointment_id] = [];
              }

              let investigationEntry = groupedData[appointment_id].find(
                (entry) => entry.investigation_id === investigation_id
              );

              if (!investigationEntry) {
                investigationEntry = {
                  investigation_id,
                  appointment_id,
                  observation: [],
                  observation_organism: [],
                };
                groupedData[appointment_id].push(investigationEntry);
              }

              investigationEntry.observation.push({
                observation_id: obs.observation_id,
                expected_revision: obs.result_revision,
                expected_calculation_context: isCalculatedObservation(obs) ? calculationContext(observations, obs.observation_id,
                  { ageYears: patientAgeForFormula, gender: headerDetails?.gender }) : undefined,
                calculation_override: obs.calculation_override || undefined,
                gender: obs.patient_details?.gender,
                observation_value: String(observationValue ?? ""),
                status: nextStatus === "SAVE" ? currentStatus : nextStatus,
                min_reading: obs.min_reading,
                max_reading: obs.max_reading,
                min_critical: obs.min_critical,
                max_critical: obs.max_critical,
                unit: obs.unit,
                barcode_no: obs.barcode_no,
                doctor_id: filterValue || doctorId,
                comments: currentComment,
                comment_id: currentCommentName?.comment_id,
                modified_by: auth?.user?.employee_code_id,
                template_id: currentTemplate?.template_id,
                template_description: currentTemplate?.template_description,
                display_reading: obs.display_reading,
              });
            });
          }
        );
      }
    );
    // console.log("observationData", observationData);
    if (observationData && observationData.length > 0) {
      Object.values(groupedData)
        .flat()
        .forEach((investigationEntry) => {
          observationData.forEach((obsData) => {
            if (obsData.parent_investigation_id !== investigationEntry.investigation_id) {
              return;
            }
            if (
              obsData.observation_value !== undefined &&
              obsData.observation_value !== null &&
              String(obsData.observation_value).trim() !== ""
            ) {
              investigationEntry.observation_organism.push({
                patient_name_id: patientNameId,
                observation_id: obsData.id,
                investigation_id: obsData?.parent_investigation_id,
                parent_investigation_id: investigationEntry.investigation_id,
                appointment_id: investigationEntry.appointment_id,
                franchise_id: Number(localStorage.getItem("franchise_id")),
                zone_id: zoneLocalStorageId(),
                appointment_investigation_id: appointmentInvestigationId,
                gender: "",
                observation_value: String(obsData.observation_value ?? ""),
                observation_value_mac_reading: String(
                  obsData.observation_value_mac_reading ?? ""
                ),
                min_reading: "",
                max_reading: "",
                min_critical: "",
                max_critical: "",
                unit: "",
                is_active: true,
                created_by: auth?.user?.employee_code_id || 0,
              });
            }
          });
        });
    }

    const payload = {
      data: Object.values(groupedData).flat(),
    };

    if (payload.data.length > 0) {
      saveInFlightRef.current = true;
      addPatientLabResultEntryMutate(payload);
    } else {
      showAlert("Select an editable investigation. Approved results must be reopened before correction.", "danger");
    }
  };

  const handleValueChange = useCallback(
    (value, department, investigation, obsId) => {
      const current = observationDraftRef.current;
      const row = current?.[department]?.[investigation]?.observations?.find(
        (obs) => Number(obs.observation_id) === Number(obsId)
      );
      if (!row || isLockedObservationStatus(row.status) || (isCalculatedObservation(row) && !isManualSelfConversion(row))) return;
      const next = updateObservationFieldInDraft(current, department, investigation, obsId, "observation_value", value);
      observationDraftRef.current = next;
      setObservationDraft(next);
    }, []
  );

  const handleObservationMetaFieldChange = useCallback(
    (department, investigation, obsId, field, value) => {
      const next = updateObservationFieldInDraft(
          observationDraftRef.current,
          department,
          investigation,
          obsId,
          field,
          value
        );
      observationDraftRef.current = next;
      setObservationDraft(next);
    },
    []
  );

  const handleCalculationOverride = useCallback(async (row, withholdAll = false) => {
    if (!canOverrideCalculation || isLockedObservationStatus(row.status)) return;
    const department = row.investigation_details.lab_department.department;
    const investigation = row.investigation_details.investigation_name;
    const original = observationDraftRef.current;
    let mode = "withhold";
    if (!withholdAll) {
      const choice = await Swal.fire({ title: "Calculated result decision", input: "select",
        inputOptions: { manual: "Use a manually reviewed value", withhold: "Withhold: do not report this result", auto: "Restore automatic calculation" },
        inputPlaceholder: "Select an action", showCancelButton: true,
        inputValidator: value => !value ? "Select an action" : undefined });
      if (!choice.isConfirmed) return;
      mode = choice.value;
    }
    let value = "";
    if (mode === "manual") {
      const answer = await Swal.fire({ title: "Reviewed result value", input: "text", showCancelButton: true,
        inputValue: row.observation_value || "",
        inputValidator: text => !/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(text.trim()) || !Number.isFinite(Number(text))
          ? "Enter a nonnegative numeric result" : undefined });
      if (!answer.isConfirmed) return;
      value = answer.value.trim();
    }
    const reason = await Swal.fire({ title: withholdAll ? "Withhold all calculated results?" : "Reason for this decision",
      text: withholdAll ? "Measured values and comments stay unchanged. Calculated results will be blank on the report." : "The decision and your identity will be recorded when you save.",
      input: "textarea", showCancelButton: true,
      inputValidator: text => text.trim().length < 5 || text.trim().length > 1000 ? "Enter a reason (5-1000 characters)" : undefined });
    if (!reason.isConfirmed) return;
    if (original !== observationDraftRef.current) {
      showAlert("The results changed while reviewing. Please review again.", "danger");
      return;
    }
    const next = cloneObservationDraft(original);
    const rows = next[department][investigation].observations;
    const targets = rows.filter(obs => isCalculatedObservation(obs) && !isLockedObservationStatus(obs.status) &&
      (withholdAll || Number(obs.observation_id) === Number(row.observation_id)));
    targets.forEach(obs => { obs.calculation_override = { mode, value, reason: reason.value.trim() }; });
    targets.forEach(obs => { obs.calculation_override.context = calculationContext(rows, obs.observation_id,
      { ageYears: patientAgeForFormula, gender: headerDetails?.gender }); });
    observationDraftRef.current = next;
    setObservationDraft(next);
  }, [canOverrideCalculation, patientAgeForFormula, headerDetails?.gender, showAlert]);

  const add_observation_columns = useMemo(() => {
    return ADD_OBSERVATION_COLUMNS_DATA(
      handleValueChange,
      inputRefs,
      [],
      [],
      null,
      [],
      null,
      null,
      checkedInvestigations,
      lastFocusedInput,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      null,
      null,
      null,
      null,
      isNavigatingWithinTableRef,
      "validated",
      null,
      ["APPROVED", "REPORT_ISSUED"],
      handleObservationMetaFieldChange,
      canOverrideCalculation ? handleCalculationOverride : null
    );
  }, [
    handleValueChange,
    checkedInvestigations,
    handleObservationMetaFieldChange,
    canOverrideCalculation,
    handleCalculationOverride,
  ]);

  const handleValue = (
    rowId,
    newValue,
    parent_investigation_id,
    fieldType = "observation_value"
  ) => {
    console.log("📥 handleValue CALLED with:", {
      rowId,
      newValue,
      parent_investigation_id,
      fieldType,
    });

    setObservationData((prevData) => {
      if (!prevData || prevData.length === 0) {
        const newRow = {
          id: rowId,
          parent_investigation_id: parent_investigation_id,
          [fieldType]: newValue,
        };
        return [newRow];
      }

      const rowExists = prevData.some(
        (row) => String(row.id) === String(rowId)
      );

      if (rowExists) {
        return prevData.map((row) =>
          String(row.id) === String(rowId)
            ? {
                ...row,
                [fieldType]: newValue,
                parent_investigation_id: parent_investigation_id, // ✅ FIXED HERE
              }
            : row
        );
      } else {
        const newRow = {
          id: rowId,
          parent_investigation_id: parent_investigation_id,
          [fieldType]: newValue,
        };
        return [...prevData, newRow];
      }
    });
  };

  const investigation_observation_columns = useMemo(() => {
    return INVESTIGATION_OBSERVATION_COLUMNS_DATA(handleValue, inputRefs);
  }, [inputRefs]);

  // Handle comment name selection for a specific investigation
  const handleCommentNameChange = (
    departmentName,
    investigationName,
    selectedCommentName
  ) => {
    const key = `${departmentName}-${investigationName}`;

    if (selectedCommentName === null) {
      // Handle clearing the dropdown
      setCommentNamesMap((prevMap) => {
        const newMap = { ...prevMap };
        delete newMap[key];
        return newMap;
      });

      setCommentsMap((prevMap) => {
        const newMap = { ...prevMap };
        delete newMap[key];
        return newMap;
      });
      return;
    }

    setCommentNamesMap((prevMap) => ({
      ...prevMap,
      [key]: selectedCommentName,
    }));

    if (selectedCommentName?.comments) {
      setCommentsMap((prevMap) => ({
        ...prevMap,
        [key]: selectedCommentName.comments,
      }));
    }
  };

  useEffect(() => {
    if (!isOpenObservationValue) {
      return;
    }

    const departmentData =
      listPayment?.data?.observations?.department_investigation;
    if (!departmentData) {
      return;
    }

    const hasDraftObservations =
      observationDraft &&
      Object.keys(observationDraft).length > 0;

    if (hasDraftObservations) {
      setIsObservationSectionReady(true);
      return;
    }

    const hasSourceObservations = Object.values(departmentData || {}).some(
      (investigations) =>
        Object.values(investigations || {}).some(
          (wrapper) => (wrapper?.observations || []).length > 0
        )
    );

    if (!hasSourceObservations) {
      setIsObservationSectionReady(true);
    }
  }, [
    isOpenObservationValue,
    listPayment?.data?.observations?.department_investigation,
    observationDraft,
  ]);

  useEffect(() => {
    if (!observationDraft || Object.keys(observationDraft).length === 0)
      return;

    const currentLoadKey = `${rowData?.appointment_id || ""}-${investigationId || ""}`;
    if (observationMetaLoadKeyRef.current === currentLoadKey) {
      return;
    }

    const newCommentsMap = {};
    const newCommentNamesMap = {};
    const extractedDoctors = {};
    const newTemplateMap = {};

    Object.entries(observationDraft).forEach(
      ([department, investigations]) => {
        Object.entries(investigations).forEach(
          ([investigation_name, observationWrapper]) => {
            const key = `${department}-${investigation_name}`;
            const observations = observationWrapper?.observations || [];

            if (observations && observations.length > 0) {
              newCommentsMap[key] = getInvestigationComment(observationWrapper);

              if (observationWrapper?.comment_obj) {
                newCommentNamesMap[key] = {
                  comment_id: observationWrapper.comment_obj.id,
                  comment_name: observationWrapper.comment_obj.comment_name,
                  comments: observationWrapper.comment_obj.comments,
                };
              } else {
                const obsWithCommentId = observations.find(
                  (obs) => obs?.comment_id
                );
                if (obsWithCommentId) {
                  newCommentNamesMap[key] = {
                    comment_id: obsWithCommentId.comment_id,
                    comment_name: obsWithCommentId.comment_name || "",
                    comments: obsWithCommentId.comments || "",
                  };
                }
              }

              observations.forEach((obs) => {
                if (obs?.doctor_obj?.id) {
                  extractedDoctors[key] = obs.doctor_obj.id;
                }
              });

              const template_id =
                observationWrapper?.template_id || observations[0]?.template_id;
              const template_description =
                observationWrapper?.template_description ||
                observations[0]?.template_description ||
                "";

              if (template_id || template_description) {
                newTemplateMap[key] = {
                  template_id,
                  template_description,
                };
              }
            }
          }
        );
      }
    );

    setCommentsMap(newCommentsMap);
    setCommentNamesMap(newCommentNamesMap);
    setTemplate(newTemplateMap);

    const firstDoctorId = Object.values(extractedDoctors)[0];
    if (firstDoctorId) {
      setdoctorId(firstDoctorId);
    }
    observationMetaLoadKeyRef.current = currentLoadKey;
  }, [observationDraft, rowData?.appointment_id, investigationId]);

  // console.log("selectedInvestigations", selectedInvestigations);

  if (!isOpenObservationValue) return null;

  if (!rowData?.appointment_id || !investigationId) {
    return <div role="alert">Select an appointment and investigation to enter results.</div>;
  }

  if (isLoadingPayment && isFetchingObservations) {
    return <Loader isLoading={isLoadingPayment} />;
  }

  const observationErrorPanel = observationError && (
    <div role="alert" className="alert alert-danger">
      {listPayment ? "Refresh failed. Previously loaded observations are shown. " : "Unable to load observations. "}
      {String(observationError?.response?.data?.detail || observationError.message || "Please retry.")}
      <button type="button" disabled={isFetchingObservations} onClick={() => refetchPayment()}>
        {isFetchingObservations ? "Retrying..." : "Retry"}
      </button>
    </div>
  );
  if (observationError && !listPayment) return observationErrorPanel;

  const shouldShowObservationSectionLoader =
    isOpenObservationValue &&
    !isObservationSectionReady &&
    !!listPayment?.data?.observations?.department_investigation;

  return (
    <>
      <div style={{ position: "relative" }}>
        {observationErrorPanel}
        {!observationError && isObservationSectionReady &&
          !hasObservationRows(observationDraft) && (
            <div role="status" className="alert alert-info">
              No observations are available. Check that this investigation was received successfully and has observation mappings.
              <button type="button" disabled={isFetchingObservations} onClick={() => refetchPayment()}>Refresh Observations</button>
            </div>
          )}
        {(isLoadingPayment || isLoading || isLoadingAdd) && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "rgba(255, 255, 255, 0.7)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10,
            }}
          >
            <Loader isLoading={true} />
          </div>
        )}

        <TitleComponent headerDetails={listPayment?.data?.header_details} />
        <div className="d-flex justify-content-between align-items-center mb-1">
          <InvestigationResults
            extraInvestigations={listPayment?.data?.extra_investigation}
            observations={listPayment?.data?.observations}
            setInvestigationId={setInvestigationId}
          />
        </div>

        {shouldShowObservationSectionLoader && (
          <div
            style={{
              minHeight: "240px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <Loader isLoading={true} />
          </div>
        )}

        {calculationSnapshot.issues.some((issue) => checkedInvestigations[`${issue.department}-${issue.investigation}`]) && (
          <div role="alert" className="alert alert-warning">
            Some calculations are incomplete. Check the highlighted inputs. You can save an incomplete draft, but approval requires valid calculations.
          </div>
        )}
        {!shouldShowObservationSectionLoader &&
          observationDraft &&
          Object.keys(observationDraft).length > 0 &&
          Object.entries(calculatedDraft).map(
            ([department, investigations]) =>
              Object.entries(investigations).map(
                ([investigation_name, observationWrapper], index) => {
                  const investigationKey = `${department}-${investigation_name}`;
                  const observations = observationWrapper?.observations || [];
                  const investigationIds =
                    observations[0]?.investigation_details?.id || null;
                  const investigationBarcode =
                    observations.find(
                      (obs) =>
                        obs?.barcode_no !== null &&
                        obs?.barcode_no !== undefined &&
                        String(obs.barcode_no).trim() !== ""
                    )?.barcode_no || "";

                  // console.log("investigation_name", investigation_name);
                  // console.log("obervations", observations);
                  return (
                    <div
                      key={`${department}-${investigation_name}`}
                      style={{ marginBottom: 20 }}
                    >
                      <div
                        className="d-flex justify-content-between align-items-center p-2 mb-2"
                        style={{ background: "#afeeee" }}
                      >
                        <p style={{ fontSize: "16px", fontWeight: "bold" }}>
                          <input
                            type="checkbox"
                            checked={Boolean(
                              checkedInvestigations[investigationKey]
                            )}
                            onChange={() =>
                              handleCheckboxChange(investigationKey)
                            }
                            style={{ marginRight: 8 }}
                          />
                          {index + 1}. {investigation_name}
                        </p>
                        <div className="d-flex align-items-center gap-3">
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: "600",
                              minWidth: "260px",
                              textAlign: "left",
                              paddingLeft: "6px",
                            }}
                          >
                            Barcode: {investigationBarcode || "-"}
                          </span>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleDocuments}
                          >
                            Add Attachment
                          </button>
                        </div>
                        {documentInfo?.data?.file_name && (
                          <span
                            style={{
                              fontSize: "15px",
                              fontWeight: "bold",
                              padding: "5px",
                              marginLeft: "10px",
                            }}
                          >
                            {documentInfo?.data?.file_name}
                          </span>
                        )}
                      </div>
                      {observations?.map((observation, obsIndex) => {
                        const reportType =
                          observation?.investigation_details?.report_type;

                        return (
                          <div key={obsIndex} style={{ marginBottom: "10px" }}>
                            {reportType === "Path RichText" ? (
                              <>
                                <div className="col-md-4">
                                  <h5
                                    style={{
                                      fontSize: "13px",
                                      marginBottom: "5px",
                                    }}
                                  >
                                    Investigation Template
                                  </h5>
                                  <TemplateDropDown
                                    investigationId={investigationIds}
                                    setFilterValue={(val) =>
                                      setTemplate((prev) => ({
                                        ...prev,
                                        [investigationKey]: {
                                          ...prev[investigationKey],
                                          ...val,
                                        },
                                      }))
                                    }
                                    template={template[investigationKey] || {}}
                                  />
                                </div>
                                <div className="form-group mt-2">
                                  <label
                                    htmlFor="commentDesc"
                                    className="form-label"
                                  >
                                    Template Description
                                  </label>
                                  <TextEditor
                                    editorValue={
                                      template[investigationKey]
                                        ?.template_description || ""
                                    }
                                    height={400}
                                    setEditorValue={(val) =>
                                      setTemplate((prev) => ({
                                        ...prev,
                                        [investigationKey]: {
                                          ...prev[investigationKey],
                                          template_description: val,
                                        },
                                      }))
                                    }
                                  />
                                </div>
                              </>
                            ) : (
                              <div key={obsIndex}>
                                {obsIndex === 0 && (
                                  <MemoizedObservationTable
                                    data={observations}
                                    tableColumns={add_observation_columns}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {isInvestigationCulture(
                        department,
                        investigation_name
                      ) && (
                        <>
                          <div
                            style={{
                              width: "100%",
                              maxWidth: "100px",
                              marginTop: "10px",
                            }}
                          >
                            <select
                              style={{ width: "100%", padding: "5px" }}
                              onChange={(e) => {
                                handleInvestigationChange(e);
                                e.target.value = "";
                              }}
                            >
                              <option value="">- Select -</option>
                              {onlyOrganismData?.data?.data?.map((item) => (
                                <option key={item.id} value={item.id}>
                                  {item.investigation_name}
                                </option>
                              ))}
                            </select>
                          </div>

                          {selectedInvestigations.map((investigation) => (
                            <div
                              key={investigation.id}
                              style={{
                                marginBottom: "20px",
                                marginTop: "20px",
                              }}
                            >
                              <div
                                style={{
                                  display: "inline-flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  padding: "5px",
                                  border: "1px solid #ccc",
                                  borderRadius: "5px",
                                  marginBottom: "5px",
                                  backgroundColor: "#f5f5f5",
                                  maxWidth: "400px",
                                  minWidth: "100px",
                                }}
                              >
                                <strong>{investigation.name}</strong>
                                <button
                                  onClick={() =>
                                    handleRemoveInvestigation(investigation.id)
                                  }
                                  style={{
                                    background: "red",
                                    color: "white",
                                    border: "none",
                                    padding: "3px 6px",
                                    cursor: "pointer",
                                    borderRadius: "3px",
                                    marginLeft: "5px",
                                  }}
                                >
                                  ✕
                                </button>
                              </div>

                              {investigationDataMap[investigation.id] && (
                                <div
                                  style={{
                                    marginTop: "10px",
                                    padding: "5px",
                                    border: "1px solid #ddd",
                                    borderRadius: "5px",
                                  }}
                                >
                                  <MemoizedObservationTable
                                    data={
                                      investigationDataMap[investigation.id]
                                    }
                                    tableColumns={
                                      investigation_observation_columns
                                    }
                                  />
                                </div>
                              )}
                            </div>
                          ))}
                        </>
                      )}
                      <div className="row mt-2">
                        <div className="col-md-4">
                          <h5 style={{ fontSize: "13px" }}>
                            Investigation Comments Name
                          </h5>
                          <CommentNameDropdown
                            investigationId={investigationIds}
                            value={commentNamesMap[investigationKey] || null}
                            setFilterValue={(selectedCommentName) =>
                              handleCommentNameChange(
                                department,
                                investigation_name,
                                selectedCommentName
                              )
                            }
                          />
                        </div>

                        <div className="col-md-8">
                          <h5 style={{ fontSize: "13px" }}>Comments</h5>
                          <div
                            className="form-control"
                            style={{
                              height: "50px",
                              overflow: "auto",
                              resize: "both",
                              whiteSpace: "pre-wrap",
                            }}
                            dangerouslySetInnerHTML={{
                              __html: commentsMap[investigationKey] ?? "",
                            }}
                          />

                          {/* <div
                            className="form-control"
                            contentEditable
                            onBlur={(e) =>
                              handleCommentChange(
                                department,
                                investigation_name,
                                e.currentTarget.innerHTML
                              )
                            }
                            dangerouslySetInnerHTML={{
                              __html: commentsMap[investigationKey] ?? "",
                            }}
                            style={{
                              minHeight: "40px",
                              resize: "both",
                              overflow: "auto",
                              padding: "8px",
                              fontSize: "13px",
                              border: "1px solid #ced4da",
                              borderRadius: "4px",
                            }}
                          ></div> */}
                        </div>
                      </div>
                    </div>
                  );
                }
              )
          )}

        <div style={{ display: "flex", gap: "20px" }}>
          <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
            {/* <div className="form-group mt-2">
              <h5 style={{ fontSize: "13px" }}>Investigation Comments Name</h5>
              <CommentNameDropdown
                investigationId={investigationId}
                setFilterValue={setCommentName}
              />
            </div> */}
            <div className="form-group">
              <h5 style={{ fontSize: "13px" }}>
                Internal Doctor{" "}
                {status === "APPROVED" && (
                  <span style={{ color: "red" }}> *</span>
                )}
              </h5>
              <InternalDoctorCheckbox
                employee_id={jwtAuthreducer?.user?.employee_code_id}
                setFilterValue={setFilterValue}
                doctorId={doctorId}
                isRequired={status === "APPROVED"}
                status={status}
              />
            </div>
            <div className="form-group mt-2" style={{ marginTop: "10px" }}>
              <h5 style={{ fontSize: "13px" }}>Status</h5>
              {reportRoleData?.data?.only_roles_data &&
                Object.keys(reportRoleData.data.only_roles_data).map(
                  (roleName) => (
                    <div
                      key={roleName}
                      style={{ display: "inline-block", marginRight: "10px" }}
                    >
                      <label>
                        <input
                          type="radio"
                          name="roleSelection"
                          value={roleName}
                          checked={status === roleName}
                          onChange={() => setStatus(roleName)}
                          style={{ marginRight: "5px" }}
                        />
                        {roleName}
                      </label>
                    </div>
                  )
                )}
            </div>
          </div>

          {/* <div className="form-group mt-2" style={{ flex: 1 }}>
            <h5 style={{ fontSize: "13px" }}>Comments</h5>
            <TextEditor editorValue={comment} height={155} setEditorValue={setComment} />
          </div> */}
        </div>
        <button
          type="submit"
          className="btn btn-primary mt-4"
          onClick={handleSave}
          // disabled={!status}
          // disabled={
          //   status === "APPROVED" && (!filterValue || filterValue === "")
          // }
          disabled={
            !hasObservationRows(observationDraft) || isLoadingAdd ||
            (status === "APPROVED" && !filterValue && !doctorId) ||
            hasNegativeObservationValue
          }
        >
          Save
        </button>
      </div>
    </>
  );
};

export default AddObservationValue;
